import { useState } from 'react';
import api from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Button } from '../ui/button';
import { Loader2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

const errText = (e, fallback) => {
  const d = e?.response?.data?.detailObject || e?.response?.data?.detail;
  return (typeof d === 'object' ? d?.message : d) || fallback;
};

/** V66 — learner pays a dietician-raised request through Razorpay (order via OPS, verified by LMS). */
export const DietPayButton = ({ requestId, amountStr, label, onPaid, className = '', size = 'sm', testId = 'diet-pay-btn' }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (busy) return;
    setBusy(true);
    // V74 — the one rule on this screen: never tell her a payment failed when the money left
    // her account. Any error path re-reads the request first and treats `paid` as success.
    const confirmFromServer = async (successMsg) => {
      try {
        const fresh = (await api.get(`/api/medical/payment-requests/${requestId}`)).data;
        if (fresh?.status === 'paid') { toast.success(successMsg); onPaid?.(fresh); return true; }
      } catch { /* fall through to the original error */ }
      return false;
    };
    try {
      const order = (await api.post(`/api/medical/payment-requests/${requestId}/order`)).data;
      if (!order.order_id || !order.razorpay_key_id || !window.Razorpay) {
        toast.error('Payment gateway unavailable. Please retry in a moment.');
        setBusy(false);
        return;
      }
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: 'Soul Sanskar Academy', description: order.label || label || 'Diet Plan',
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.phone || '' },
        theme: { color: '#1a3a36' },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (resp) => {
          try {
            const v = await api.post(`/api/medical/payment-requests/${requestId}/verify`, {
              order_id: order.order_id, payment_id: resp.razorpay_payment_id, signature: resp.razorpay_signature,
            });
            if (v.data.verified) { toast.success('Payment received — thank you!'); onPaid?.(v.data.request); }
            else if (!(await confirmFromServer('Payment received — thank you!'))) {
              toast.error('Payment verification failed. Our team will check and get back to you.');
            }
          } catch (e) {
            if (!(await confirmFromServer('Payment received — thank you!'))) {
              toast.error(errText(e, 'Payment verification error. If you were charged, our team will sort it out.'));
            }
          }
          setBusy(false);
        },
      });
      rzp.open();
    } catch (e) {
      const code = e?.response?.data?.detail?.code || e?.response?.data?.detailObject?.code;
      if (code === 'ALREADY_PAID') {
        await confirmFromServer('This is already paid — your nutritionist has it.');
      } else {
        toast.error(errText(e, 'Could not start the payment'));
      }
      setBusy(false);
    }
  };

  return (
    <Button size={size} onClick={pay} disabled={busy} className={`bg-emerald-600 hover:bg-emerald-700 text-white ${className}`} data-testid={testId}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CreditCard className="h-3.5 w-3.5 mr-1" />Pay {amountStr}</>}
    </Button>
  );
};
